import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError, safeStorage } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  // null = checking, false = unauthenticated, object = user
  const [user, setUser] = useState(null);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      // 401 is expected when not logged in — only surface non-auth errors.
      if (err?.response?.status && err.response.status !== 401) {
        console.warn("Auth check failed:", err);
      }
      setUser(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    try {
      const cleanEmail = (email || "").trim().toLowerCase();
      const { data } = await api.post("/auth/login", { email: cleanEmail, password });
      if (data?.token) safeStorage.set("access_token", data.token);
      setUser({
        id: data.id,
        email: data.email,
        name: data.name,
        role: data.role,
        location_id: data.location_id,
        created_at: data.created_at,
      });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Network failure during logout is non-fatal — we still clear local state.
      console.warn("Logout request failed (continuing with local cleanup):", err);
    }
    safeStorage.remove("access_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
